import cx from 'classnames';
import { Checkbox, CheckboxGroup, Skeleton } from "@heroui/react";
import { CategorizedPrivileges, LoginPrivileges } from '@/features/logins/loginsSlice';
import { useTranslation } from 'react-i18next';

export interface CategorizedPrivilegeDropdownProps {
  label?: string;
  placeholder?: string;
  privileges: CategorizedPrivileges;
  selectedPrivileges: string[];
  onSelectionChange: (selected: string[]) => void;
  isLoaded?: boolean;
  errorMessage?: string;
  isInvalid?: boolean;
  className?: string;
  disabledPrivileges?: string[];
  disabledReason?: string;
}

const CategorizedPrivilegeDropdown = (props: CategorizedPrivilegeDropdownProps) => {
  const { t } = useTranslation();
  const {
    label,
    privileges,
    selectedPrivileges,
    onSelectionChange,
    isLoaded = true,
    errorMessage,
    isInvalid,
    className,
    disabledPrivileges,
    disabledReason
  } = props;

  const isDisabled = (id: string) => !!disabledPrivileges?.includes(id);

  const handleSelectionChange = (vals: string[]) => {
    onSelectionChange(vals);
  };

  const categoriesList = privileges || [];

  const classes = cx(
    'flex flex-col w-full',
    className
  );

  const out = (
    <div className={classes}>
      {label && (
        <label className="text-sm text-black mb-2">{label}</label>
      )}
      <div className="border border-gray-300 rounded-[4px]">
        <CheckboxGroup
          value={selectedPrivileges}
          onValueChange={handleSelectionChange}
          aria-label="select privileges"
          isInvalid={isInvalid}
          errorMessage={errorMessage}
        >
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 sticky top-0 z-10">
                <th className="p-3 text-left text-sm font-semibold text-black border-b border-gray-300 w-[30%]">{t('users:privileges:name')}</th>
                <th className="p-3 text-left text-sm font-semibold text-black border-b border-gray-300">{t('users:privileges:description')}</th>
                <th className="p-3 text-center text-sm font-semibold text-black border-b border-gray-300 w-[70px]">{t('users:privileges:select')}</th>
              </tr>
            </thead>
            <tbody>
              {categoriesList.map((cat) => (
                <>
                  <tr key={`cat-${cat.category}`} className="bg-[#0078d7]">
                    <td colSpan={3} className="px-3 py-2 text-[14px] font-semibold text-white">
                      {t(`users.privileges.categories.${cat.category}`,cat.category)}
                    </td>
                  </tr>
                  {(cat.privileges || []).map((priv: LoginPrivileges) => (
                    <tr key={priv.id}
                        title={isDisabled(priv.id) ? disabledReason : undefined}
                        className={`border-b border-gray-200 hover:bg-gray-50 ${isDisabled(priv.id) ? 'opacity-60' : ''}`}>
                      <td className="p-3 text-sm text-black">{priv.id}</td>
                      <td className="p-3 text-sm text-black">
                        {isDisabled(priv.id) && disabledReason ? disabledReason : priv.desc}
                      </td>
                      <td className="p-3 text-center">
                        <Checkbox
                          value={priv.id}
                          isDisabled={isDisabled(priv.id)}
                          classNames={{
                            base: "m-0",
                            wrapper: "m-0",
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </CheckboxGroup>
      </div>
      {isInvalid && errorMessage && (
        <span className="text-red-500 text-xs mt-1">{errorMessage}</span>
      )}
    </div>
  );

  if (!isLoaded) {
    return (
      <Skeleton isLoaded={isLoaded} className="w-full">
        {out}
      </Skeleton>
    );
  }

  return out;
};

export default CategorizedPrivilegeDropdown;
